import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';
import QRCodeGenerator from './QRCodeGenerator';
import AssignmentBulkOperations from './AssignmentBulkOperations';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import { formatDateForInputIST, formatDisplayDateIST, formatDisplayDateTimeIST, getTodayInputDateIST } from '../utils/dateUtils';

const API_URL = '/api';

function AssetAssignments() {
  const getInitialFormData = () => ({
    employeeId: '',
    selectedAssets: [],
    assignedDate: getTodayInputDateIST(),
    deliveryMethod: 0,
    deliveryStatus: 0,
    courierService: '',
    trackingId: '',
    pickupPersonName: '',
    pickupPersonMobile: '',
    notes: ''
  });

  const [assignments, setAssignments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    assetType: '',
    employee: '',
    deliveryStatus: '',
    status: '',
    dateRange: { start: '', end: '' }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [assetFilter, setAssetFilter] = useState('');
  const [formData, setFormData] = useState(getInitialFormData);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    fromEmployeeId: '',
    assignmentId: '',
    toEmployeeId: '',
    notes: ''
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineAssetId, setTimelineAssetId] = useState(null);
  const [assetTimeline, setAssetTimeline] = useState([]);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [groupByEmployee, setGroupByEmployee] = useState(false);
  const [groupedCurrentPage, setGroupedCurrentPage] = useState(1);
  const [groupedItemsPerPage] = useState(10);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const { alert, showAlert, hideAlert } = useAlert();


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAssignments(),
        fetchAvailableAssets(),
        fetchEmployees()
      ]);
      setLoading(false);
    };
    loadData();
    document.title = 'Asset Assignments - Saral Asset Manager';
  }, []);

  useEffect(() => {
    if (groupByEmployee && assignments.length > 0) {
      const filtered = assignments.filter(assignmentMatchesFilters);
      const validIds = new Set(filtered.map(a => a.employeeId));
      setExpandedEmployees(prev => new Set([...prev].filter(id => validIds.has(id))));
      setGroupedCurrentPage(1);
    }
  }, [groupByEmployee, filters, assignments]);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_URL}/assetassignments`);
      // Sort by assignment date (newest first)
      const sorted = response.data
        .filter(a => a.asset?.status !== 'Dead' && a.asset?.Status !== 'Dead')
        .sort((a, b) => new Date(b.assignedDate) - new Date(a.assignedDate));
      setAssignments(sorted);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchAvailableAssets = async () => {
    try {
      const response = await axios.get(`${API_URL}/assets`);
      const availableAssets = response.data.filter(asset => {
        const assigned = asset.assignedCount || 0;
        const dead = asset.deadCount || 0;
        const remaining = asset.quantity - assigned - dead;
        return remaining > 0 && asset.status !== 'Dead';
      });
      setAssets(availableAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      console.log('Employees data:', response.data); // Debug log
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log('Submitting assignments:', formData); // Debug log

      if (editingAssignment) {
        const payload = {
          assignedDate: formData.assignedDate || null,
          serialNumber: formData.selectedAssets[0]?.serialNumber?.trim() || null,
          deliveryMethod: formData.deliveryMethod,
          deliveryStatus: formData.deliveryStatus,
          courierService: formData.courierService || null,
          trackingId: formData.trackingId || null,
          pickupPersonName: formData.pickupPersonName || null,
          pickupPersonMobile: formData.pickupPersonMobile || null,
          notes: formData.notes || null
        };
        await axios.put(`${API_URL}/assetassignments/${editingAssignment.id}`, payload);
      } else {
        await Promise.all(
          formData.selectedAssets.map(assetData => {
            const payload = {
              assetId: parseInt(assetData.assetId),
              employeeId: formData.employeeId,
              serialNumber: assetData.serialNumber,
              assignedDate: formData.assignedDate || null,
              deliveryMethod: formData.deliveryMethod,
              courierService: formData.courierService,
              trackingId: formData.trackingId,
              pickupPersonName: formData.pickupPersonName,
              pickupPersonMobile: formData.pickupPersonMobile,
              notes: formData.notes
            };
            console.log('Assignment payload:', payload); // Debug log
            return axios.post(`${API_URL}/assetassignments`, payload);
          })
        );
      }

      handleCloseModal();
      showAlert('success', editingAssignment ? 'Assignment updated successfully!' : `${formData.selectedAssets.length} asset(s) assigned successfully!`);
      fetchAssignments();
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error creating assignment:', error);
      console.error('Error response:', error.response?.data); // Debug error details
      const errorMsg = error.response?.data || error.message || 'Failed to assign assets';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setEditingAssignment(null);
    setFormData(getInitialFormData());
    setShowModal(true);
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      employeeId: assignment.employeeId || assignment.EmployeeId || '',
      selectedAssets: [{
        assetId: assignment.assetId,
        assetName: assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset',
        assetType: getAssetTypeName(assignment),
        serialNumber: assignment.serialNumber || ''
      }],
      assignedDate: formatDateForInputIST(assignment.assignedDate || assignment.AssignedDate) || getTodayInputDateIST(),
      deliveryMethod: assignment.deliveryMethod ?? assignment.DeliveryMethod ?? 0,
      deliveryStatus: assignment.deliveryStatus ?? assignment.DeliveryStatus ?? 0,
      courierService: assignment.courierService || assignment.CourierService || '',
      trackingId: assignment.trackingId || assignment.TrackingId || '',
      pickupPersonName: assignment.pickupPersonName || assignment.PickupPersonName || '',
      pickupPersonMobile: assignment.pickupPersonMobile || assignment.PickupPersonMobile || '',
      notes: assignment.notes || assignment.Notes || ''
    });
    setShowModal(true);
  };



  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAssignment(null);
    setFormData(getInitialFormData());
  };

  const handleReturn = async (assignmentId) => {
    try {
      await axios.put(`${API_URL}/assetassignments/${assignmentId}/return`);
      showAlert('success', 'Asset returned successfully!');
      fetchAssignments();
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error returning asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to return asset';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleDelete = async (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      try {
        await axios.delete(`${API_URL}/assetassignments/${assignmentId}`);
        showAlert('success', 'Assignment deleted successfully!');
        fetchAssignments();
        fetchAvailableAssets();
      } catch (error) {
        console.error('Error deleting assignment:', error);
        const errorMsg = error.response?.data || error.message || 'Failed to delete assignment';
        showAlert('danger', `Error: ${errorMsg}`);
      }
    }
  };

  const handleDeleteAll = async () => {
    try {
      await Promise.all(assignments.map(a => axios.delete(`${API_URL}/assetassignments/${a.id}`)));
      setShowDeleteAllConfirm(false);
      showAlert('success', 'All assignments deleted successfully!');
      fetchAssignments();
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error deleting all assignments:', error);
      showAlert('danger', 'Failed to delete all assignments');
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/assetassignments/${id}`)));
      setShowDeleteSelectedConfirm(false);
      setSelectedIds([]);
      showAlert('success', `${selectedIds.length} assignment(s) deleted successfully!`);
      fetchAssignments();
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error deleting selected assignments:', error);
      showAlert('danger', 'Failed to delete selected assignments');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAssignments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAssignments.map(a => a.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleViewTimeline = async (assignment) => {
    try {
      const response = await axios.get(`${API_URL}/assets/${assignment.assetId}/timeline`, {
        params: {
          saralAssetId: assignment.saralAssetId || undefined,
          serialNumber: assignment.serialNumber || undefined
        }
      });
      setAssetTimeline(response.data);
      setTimelineAssetId(assignment.assetId);
      setShowTimelineModal(true);
    } catch (error) {
      console.error('Error fetching asset timeline:', error);
    }
  };

  const handleTransfer = (assignment) => {
    setTransferData({
      fromEmployeeId: assignment.employeeId,
      assignmentId: assignment.id,
      toEmployeeId: '',
      notes: ''
    });
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log('Transfer data before validation:', transferData); // Debug log
      
      if (!transferData.toEmployeeId || !transferData.assignmentId) {
        console.error('Missing required fields:', {
          toEmployeeId: transferData.toEmployeeId,
          assignmentId: transferData.assignmentId
        });
        return;
      }
      
      const payload = {
        assignmentId: parseInt(transferData.assignmentId),
        toEmployeeId: transferData.toEmployeeId, // Keep as string, don't parse
        notes: transferData.notes
      };
      
      console.log('Transfer payload:', payload); // Debug log
      
      await axios.post(`${API_URL}/assetassignments/transfer`, payload);
      setShowTransferModal(false);
      setTransferData({ fromEmployeeId: '', assignmentId: '', toEmployeeId: '', notes: '' });
      fetchAssignments();
    } catch (error) {
      console.error('Error transferring asset:', error);
      console.error('Error response:', error.response?.data); // Debug error details
    } finally {
      setSaving(false);
    }
  };

  const getRowClass = (item) => {
    if (item.isGroup) return 'table-light fw-bold';
    if (!item.isActive) return 'table-secondary';
    const statusClasses = {
      0: 'table-warning',
      1: 'table-info', 
      2: 'table-success'
    };
    return statusClasses[item.deliveryStatus] || '';
  };

  function getAssetTypeName(assignment) {
    const asset = assignment?.asset || assignment?.Asset;
    const assetType = asset?.assetType ?? asset?.AssetType;
    if (typeof assetType === 'string') return assetType;
    return assetType?.name || assetType?.Name || '';
  }

  function getAssignedDate(assignment) {
    return assignment?.assignedDate || assignment?.AssignedDate;
  }

  function getIsActive(assignment) {
    return assignment?.isActive ?? assignment?.IsActive;
  }

  function getDeliveryStatus(assignment) {
    return assignment?.deliveryStatus ?? assignment?.DeliveryStatus;
  }

  function getEmployeeId(assignment) {
    return assignment?.employeeId ?? assignment?.EmployeeId;
  }

  function assignmentMatchesFilters(assignment) {
    if (filters.assetType && getAssetTypeName(assignment) !== filters.assetType) return false;
    if (filters.employee && getEmployeeId(assignment) != filters.employee) return false;
    if (filters.deliveryStatus !== '' && getDeliveryStatus(assignment) != filters.deliveryStatus) return false;
    if (filters.status !== '' && (filters.status === 'active' ? !getIsActive(assignment) : getIsActive(assignment))) return false;

    const assignedDate = getAssignedDate(assignment);
    if (filters.dateRange.start && new Date(assignedDate) < new Date(filters.dateRange.start)) return false;
    if (filters.dateRange.end && new Date(assignedDate) > new Date(filters.dateRange.end)) return false;
    return true;
  }

  const filteredAssignments = assignments.filter(assignmentMatchesFilters);
  const groupedEmployeeIds = [...new Set(filteredAssignments.map(a => a.employeeId))];
  const areAllGroupedExpanded = groupedEmployeeIds.length > 0 && groupedEmployeeIds.every(id => expandedEmployees.has(id));

  const columns = [
    { 
      field: 'checkbox', 
      label: (
        <input 
          type="checkbox" 
          checked={selectedIds.length > 0 && selectedIds.length === filteredAssignments.length}
          onChange={toggleSelectAll}
        />
      ),
      render: (value, item) => (
        <input 
          type="checkbox" 
          checked={selectedIds.includes(item.id)}
          onChange={() => toggleSelect(item.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    { field: 'saralAssetId', label: 'Saral Asset ID' },
    { 
      field: 'asset', 
      label: 'Asset',
      render: (value) => value?.name || value?.Name || 'N/A',
      sortValue: (value, row) => (value?.name || value?.Name || row?.asset?.name || row?.asset?.Name || '').toString().toLowerCase()
    },
    { field: 'serialNumber', label: 'Serial Number' },
    {
      field: 'employeeName',
      label: 'Employee',
      render: (value, row) => value || row?.EmployeeName || row?.employeeId || row?.EmployeeId || 'Unknown',
      sortValue: (value, row) => (value || row?.EmployeeName || row?.employeeId || row?.EmployeeId || '').toString().toLowerCase()
    },
    { 
      field: 'deliveryMethod', 
      label: 'Delivery Method',
      render: (value) => ['Self Pickup', 'Courier', 'Other Person', 'Transfer'][value] || value,
      sortValue: (value) => Number(value ?? -1)
    },
    { 
      field: 'deliveryStatus', 
      label: 'Delivery Status',
      render: (value) => {
        const statusMap = {
          0: { text: 'Pending', class: 'bg-warning text-dark' },
          1: { text: 'In Transit', class: 'bg-info text-white' },
          2: { text: 'Delivered', class: 'bg-success text-white' }
        };
        const status = statusMap[value] || { text: value, class: 'bg-secondary' };
        return <span className={`badge ${status.class}`}>{status.text}</span>;
      },
      sortValue: (value) => Number(value ?? -1)
    },
    { 
      field: 'assignedDate', 
      label: 'Assigned Date',
      render: (value) => formatDisplayDateIST(value),
      sortValue: (value) => new Date(value).getTime()
    },
    { 
      field: 'returnDate', 
      label: 'Return Date',
      render: (value) => value ? formatDisplayDateIST(value) : '-',
      sortValue: (value) => value ? new Date(value).getTime() : 0
    },
    { 
      field: 'isActive', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
          {value ? 'Active' : 'Returned'}
        </span>
      ),
      sortValue: (value) => value ? 1 : 0
    }
  ];

  const handleMarkDelivered = async (assignmentId) => {
    try {
      await axios.put(`${API_URL}/assetassignments/${assignmentId}/deliver`);
      fetchAssignments();
    } catch (error) {
      console.error('Error marking as delivered:', error);
    }
  };

  const closeDropdownMenu = (event) => {
    if (event?.currentTarget) {
      const dropdown = event.currentTarget.closest('.dropdown');
      dropdown?.querySelector('.dropdown-menu.show')?.classList.remove('show');
      const toggle = dropdown?.querySelector('[data-bs-toggle="dropdown"]');
      toggle?.setAttribute('aria-expanded', 'false');
    }
  };

  const customActions = (item, closeMenu = () => {}) => (
    <>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); setSelectedAssignment(item); setShowQRModal(true); }}>
          <i className="fas fa-qrcode me-2"></i>QR Code
        </button>
      </li>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); handleViewTimeline(item); }}>
          <i className="fas fa-history me-2"></i>Timeline
        </button>
      </li>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); handleEdit(item); }}>
          <i className="fas fa-edit me-2"></i>Edit Details
        </button>
      </li>
      {item.isActive && (
        <>
          {item.deliveryStatus === 1 && (
            <li>
              <button className="dropdown-item" onClick={() => { closeMenu(); handleMarkDelivered(item.id); }}>
                <i className="fas fa-check me-2 text-success"></i>Mark Delivered
              </button>
            </li>
          )}
          {item.deliveryStatus !== 0 && (
            <>
              <li>
                <button className="dropdown-item" onClick={() => { closeMenu(); handleTransfer(item); }}>
                  <i className="fas fa-exchange-alt me-2 text-info"></i>Transfer
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={() => { closeMenu(); handleReturn(item.id); }}>
                  <i className="fas fa-undo me-2 text-warning"></i>Return
                </button>
              </li>
            </>
          )}
        </>
      )}
    </>
  );

  const toggleAllEmployees = () => {
    const filtered = assignments.filter(assignmentMatchesFilters);
    const allEmployeeIds = [...new Set(filtered.map(a => a.employeeId))];
    
    if (expandedEmployees.size === allEmployeeIds.length) {
      setExpandedEmployees(new Set());
    } else {
      setExpandedEmployees(new Set(allEmployeeIds));
    }
  };

  const toggleEmployee = (employeeId) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="page-header">
        <h1 className="page-title mb-0">Asset Assignments</h1>
        <div className="page-header-actions">
          {!groupByEmployee && selectedIds.length > 0 && (
            <button 
              className="btn btn-sm btn-danger"
              onClick={() => setShowDeleteSelectedConfirm(true)}
            >
              <i className="fas fa-trash-alt me-1"></i>Delete Selected ({selectedIds.length})
            </button>
          )}
          <button 
            className={`btn btn-sm ${groupByEmployee ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              const nextGroupBy = !groupByEmployee;
              setGroupByEmployee(nextGroupBy);
              if (nextGroupBy) {
                setExpandedEmployees(new Set());
                setGroupedCurrentPage(1);
              }
              setSelectedIds([]);
            }}
          >
            <i className={`fas fa-${groupByEmployee ? 'users' : 'list'} me-1`}></i>
            {groupByEmployee ? 'Grouped' : 'List'}
          </button>
          <button className="btn btn-sm btn-outline-info" onClick={() => setShowBulkModal(true)}>
            <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
          </button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setShowTransferModal(true)}>
            <i className="fas fa-exchange-alt me-1"></i><span className="d-none d-sm-inline">Transfer Asset</span><span className="d-sm-none">Transfer</span>
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Assign Assets</span><span className="d-sm-none">Assign</span>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Filters</h6>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className={`fas fa-${showFilters ? 'minus' : 'plus'}`}></i>
          </button>
        </div>
        {showFilters && (
          <div className="card-body">
            <div className="row g-3">
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label">Asset Type</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Asset Types' },
                    ...Array.from(new Set([
                      ...assignments.map(getAssetTypeName),
                      ...assets.map(a => a.assetType?.name || a.assetTypeName || a.AssetType?.Name || a.AssetType || '')
                    ].filter(Boolean)))
                      .sort((a, b) => a.localeCompare(b))
                      .map(typeName => ({ value: typeName, label: typeName }))
                  ]}
                  value={filters.assetType}
                  onChange={(e) => setFilters({...filters, assetType: e.target.value})}
                  placeholder="Select Asset Type"
                />
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label">Employee</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Employees' },
                    ...employees.map(emp => ({
                      value: emp.id ?? emp.Id,
                      label: emp.userName || emp.UserName || emp.name || emp.Name || (emp.id ?? emp.Id)
                    }))
                  ]}
                  value={filters.employee}
                  onChange={(e) => setFilters({...filters, employee: e.target.value})}
                  placeholder="Select Employee"
                />
              </div>
              <div className="col-lg-2 col-md-4 col-sm-6">
                <label className="form-label">Delivery Status</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Status' },
                    { value: '0', label: 'Pending' },
                    { value: '1', label: 'In Transit' },
                    { value: '2', label: 'Delivered' }
                  ]}
                  value={filters.deliveryStatus}
                  onChange={(e) => setFilters({...filters, deliveryStatus: e.target.value})}
                  placeholder="Select Status"
                />
              </div>
              <div className="col-lg-2 col-md-6 col-sm-6">
                <label className="form-label">Assignment Status</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'returned', label: 'Returned' }
                  ]}
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  placeholder="Select Status"
                />
              </div>
              <div className="col-lg-2 col-md-6 col-12">
                <label className="form-label">Date Range</label>
                <div className="d-flex gap-1">
                  <input 
                    type="date" 
                    className="form-control form-control-sm"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({...filters, dateRange: {...filters.dateRange, start: e.target.value}})}
                  />
                  <input 
                    type="date" 
                    className="form-control form-control-sm"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({...filters, dateRange: {...filters.dateRange, end: e.target.value}})}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 d-flex flex-wrap gap-2">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setFilters({assetType: '', employee: '', deliveryStatus: '', status: '', dateRange: {start: '', end: ''}})}
              >
                <i className="fas fa-eraser me-1"></i>Clear Filters
              </button>
              <span className="badge bg-info ms-auto">
                {filteredAssignments.length} of {assignments.length} assignments
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="table-loader p-4">
              <Loader text="Loading assignments..." size="medium" />
            </div>
          ) : groupByEmployee ? (
            <div className="p-3">
              <div className="mb-3">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleAllEmployees}
                >
                  <i className={`fas fa-${areAllGroupedExpanded ? 'minus' : 'plus'}-square me-1`}></i>
                  {areAllGroupedExpanded ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
              {(() => {
                const grouped = filteredAssignments.reduce((acc, a) => {
                  if (!acc[a.employeeId]) acc[a.employeeId] = [];
                  acc[a.employeeId].push(a);
                  return acc;
                }, {});
                
                const entries = Object.entries(grouped);
                if (entries.length === 0) return <div className="text-center py-5"><p>No assignments</p></div>;
                const totalPages = Math.max(1, Math.ceil(entries.length / groupedItemsPerPage));
                const currentPage = Math.min(groupedCurrentPage, totalPages);
                const pageStart = (currentPage - 1) * groupedItemsPerPage;
                const pagedEntries = entries.slice(pageStart, pageStart + groupedItemsPerPage);
                return (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">
                        Showing {pagedEntries.length} of {entries.length} employees
                      </small>
                      <small className="text-muted">Page {currentPage} / {totalPages}</small>
                    </div>
                    {pagedEntries.map(([empId, empAssignments]) => {
                const empName = empAssignments[0]?.employeeName || empAssignments[0]?.EmployeeName || empId;
                const isDark = document.body.getAttribute('data-theme') === 'dark';
                return (
                  <div key={empId} className="card mb-3 shadow-sm" style={{border: '1px solid #dee2e6', overflow: 'visible'}}>
                    <div 
                      className="card-header border-bottom d-flex justify-content-between align-items-center" 
                      style={{
                        cursor: 'pointer', 
                        borderRadius: expandedEmployees.has(empId) ? '0.375rem 0.375rem 0 0' : '0.375rem',
                        backgroundColor: isDark ? '#374151' : '#f8f9fa',
                        color: isDark ? '#e2e8f0' : '#212529'
                      }}
                      onClick={() => toggleEmployee(empId)}
                    >
                      <div>
                        <i className={`fas fa-chevron-${expandedEmployees.has(empId) ? 'down' : 'right'} me-2`}></i>
                        <i className="fas fa-user me-2"></i>
                        <strong>{empName}</strong>
                      </div>
                      <span className="badge bg-primary text-white">{empAssignments.length} assets</span>
                    </div>
                    {expandedEmployees.has(empId) && (
                      <div className="card-body p-0" style={{overflow: 'visible'}}>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Saral Asset ID</th>
                                <th>Asset</th>
                                <th>Serial Number</th>
                                <th>Delivery Method</th>
                                <th>Status</th>
                                <th>Assigned Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {empAssignments.map((assignment) => (
                                <tr key={assignment.id}>
                                  <td>{assignment.saralAssetId}</td>
                                  <td>{assignment.asset?.name || 'N/A'}</td>
                                  <td>{assignment.serialNumber}</td>
                                  <td>{['Self Pickup', 'Courier', 'Other Person', 'Transfer'][assignment.deliveryMethod]}</td>
                                  <td>
                                    <span className={`badge ${assignment.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                      {assignment.isActive ? 'Active' : 'Returned'}
                                    </span>
                                  </td>
                                  <td>{formatDisplayDateIST(assignment.assignedDate)}</td>
                                  <td>
                                    <div className="dropdown">
                                      <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown">
                                        <i className="fas fa-ellipsis-v"></i>
                                      </button>
                                      <ul className="dropdown-menu dropdown-menu-end">
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); setSelectedAssignment(assignment); setShowQRModal(true); }}>
                                            <i className="fas fa-qrcode me-2"></i>QR Code
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleViewTimeline(assignment); }}>
                                            <i className="fas fa-history me-2"></i>Timeline
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleEdit(assignment); }}>
                                            <i className="fas fa-edit me-2"></i>Edit Details
                                          </button>
                                        </li>
                                        {assignment.isActive && assignment.deliveryStatus === 1 && (
                                          <li>
                                            <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleMarkDelivered(assignment.id); }}>
                                              <i className="fas fa-check me-2 text-success"></i>Mark Delivered
                                            </button>
                                          </li>
                                        )}
                                        {assignment.isActive && assignment.deliveryStatus !== 0 && (
                                          <>
                                            <li>
                                              <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleTransfer(assignment); }}>
                                                <i className="fas fa-exchange-alt me-2 text-info"></i>Transfer
                                              </button>
                                            </li>
                                            <li>
                                              <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleReturn(assignment.id); }}>
                                                <i className="fas fa-undo me-2 text-warning"></i>Return
                                              </button>
                                            </li>
                                          </>
                                        )}
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                          <button className="dropdown-item text-danger" onClick={(e) => { closeDropdownMenu(e); handleDelete(assignment.id); }}>
                                            <i className="fas fa-trash me-2"></i>Delete
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
                    })}
                    {totalPages > 1 && (
                      <nav>
                        <ul className="pagination justify-content-center mt-2">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const maxVisible = 5;
                            const halfVisible = Math.floor(maxVisible / 2);
                            let startPage = Math.max(1, currentPage - halfVisible);
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                            
                            if (endPage - startPage < maxVisible - 1) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }
                            
                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => setGroupedCurrentPage(i)}>{i}</button>
                                </li>
                              );
                            }
                            return pages;
                          })()}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <DataTable
              data={filteredAssignments}
              columns={columns}
              customActions={customActions}
              onDelete={handleDelete}
              getRowClass={getRowClass}
            />
          )}
        </div>
      </div>

      {/* Unified Assignment Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">
                <i className="fas fa-magic me-2"></i>
                {editingAssignment ? 'Edit Assignment Details' : 'Assign Assets to Employee'}
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        {editingAssignment ? (
                          <>
                            <label className="form-label fw-bold text-primary">
                              <i className="fas fa-user me-2"></i>Assigned Employee
                            </label>
                            <div className="form-control bg-light">
                              {editingAssignment.employeeName || editingAssignment.EmployeeName || editingAssignment.employeeId || editingAssignment.EmployeeId || 'Unknown'}
                            </div>
                            <small className="text-muted">Use transfer if you need to move this asset to a different employee.</small>
                          </>
                        ) : (
                          <>
                            <label className="form-label fw-bold text-primary">
                              <i className="fas fa-user me-2"></i>Select Employee
                            </label>
                            <SearchableSelect
                              options={employees.map(employee => ({
                                value: employee.id ?? employee.Id,
                                label: employee.userName || employee.UserName || employee.name || employee.Name || (employee.id ?? employee.Id)
                              }))}
                              value={formData.employeeId}
                              onChange={(e) => {
                                console.log('Employee selected:', e.target.value, 'Type:', typeof e.target.value);
                                setFormData({ ...formData, employeeId: e.target.value });
                              }}
                              placeholder="Choose an employee..."
                              required
                            />
                            <small className="text-muted">Selected: {formData.employeeId || 'none'} | Type: {typeof formData.employeeId}</small>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {!editingAssignment ? (
                <div className="row">
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                          <i className="fas fa-boxes me-2"></i>Available Assets
                        </h6>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filter assets..."
                          value={assetFilter}
                          onChange={(e) => setAssetFilter(e.target.value)}
                          style={{maxWidth: '200px'}}
                        />
                      </div>
                      <div className="card-body p-3" style={{height: '350px', overflowY: 'auto'}}>
                        {assets.filter(asset => 
                          asset.name.toLowerCase().includes(assetFilter.toLowerCase()) ||
                          asset.assetType?.name.toLowerCase().includes(assetFilter.toLowerCase())
                        ).map(asset => (
                          <div
                            key={asset.id}
                            className="card mb-3 shadow-sm border-0"
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', JSON.stringify({
                                assetId: asset.id,
                                assetName: asset.name,
                                assetType: asset.assetType?.name
                              }));
                            }}
                            style={{
                              cursor: 'grab',
                              userSelect: 'none',
                              background: document.body.getAttribute('data-theme') === 'dark' 
                                ? 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)' 
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              transition: 'transform 0.2s',
                              border: document.body.getAttribute('data-theme') === 'dark' ? '1px solid #4a5568' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-1 fw-bold">{asset.name}</h6>
                                  <small className="opacity-75">
                                    <i className="fas fa-tag me-1"></i>{asset.assetType?.name}
                                  </small>
                                </div>
                                <div className="text-end">
                                  <span className="badge" style={{
                                    backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#e2e8f0' : '#f8f9fa',
                                    color: document.body.getAttribute('data-theme') === 'dark' ? '#1a202c' : '#495057'
                                  }}>
                                    <i className="fas fa-cube me-1"></i>
                                    {asset.quantity - (asset.assignedCount || 0)}
                                  </span>
                                  <br />
                                  <small className="opacity-75">available</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {assets.filter(asset => 
                          asset.name.toLowerCase().includes(assetFilter.toLowerCase()) ||
                          asset.assetType?.name.toLowerCase().includes(assetFilter.toLowerCase())
                        ).length === 0 && (
                          <div className="text-center text-muted py-5">
                            <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                            <p>{assetFilter ? 'No assets match your filter' : 'No assets available'}</p>
                          </div>
                        )}
                      </div>
                      <div className="card-footer bg-light text-center">
                        <small className="text-muted">
                          <i className="fas fa-hand-paper me-1"></i>Drag assets to assign →
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card h-100 border-success">
                      <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-check-circle me-2"></i>Selected Assets ({formData.selectedAssets.length})
                        </h6>
                      </div>
                      <div
                        className="card-body p-3"
                        style={{height: '350px', overflowY: 'auto'}}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const assetData = JSON.parse(e.dataTransfer.getData('text/plain'));
                          if (!formData.selectedAssets.some(a => a.assetId === assetData.assetId)) {
                            setFormData({
                              ...formData,
                              selectedAssets: [...formData.selectedAssets, { ...assetData, serialNumber: '' }]
                            });
                          }
                        }}
                      >
                        {formData.selectedAssets.length === 0 ? (
                          <div className="text-center text-muted py-5" style={{border: '2px dashed #28a745', borderRadius: '10px', backgroundColor: '#f8fff9'}}>
                            <i className="fas fa-cloud-download-alt fa-3x mb-3 text-success opacity-50"></i>
                            <h6>Drop Assets Here</h6>
                            <p className="mb-0">Drag assets from the left panel</p>
                          </div>
                        ) : (
                          formData.selectedAssets.map((assetData, index) => (
                            <div key={assetData.assetId} className="card mb-3 border-0 shadow-sm" style={{backgroundColor: '#f8fff9'}}>
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="mb-1 text-success fw-bold">{assetData.assetName}</h6>
                                    <small className="text-muted">
                                      <i className="fas fa-tag me-1"></i>{assetData.assetType}
                                    </small>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger rounded-circle"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        selectedAssets: formData.selectedAssets.filter((_, i) => i !== index)
                                      });
                                    }}
                                    title="Remove asset"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text bg-success text-white">
                                    <i className="fas fa-barcode"></i>
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter serial number"
                                    value={assetData.serialNumber}
                                    onChange={(e) => {
                                      const updated = [...formData.selectedAssets];
                                      updated[index].serialNumber = e.target.value;
                                      setFormData({ ...formData, selectedAssets: updated });
                                    }}
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <div className="card border-success">
                        <div className="card-header bg-success text-white">
                          <h6 className="mb-0">
                            <i className="fas fa-box me-2"></i>Assigned Asset
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1 text-success fw-bold">{formData.selectedAssets[0]?.assetName || 'Unknown Asset'}</h6>
                              <small className="text-muted d-block">
                                <i className="fas fa-tag me-1"></i>{formData.selectedAssets[0]?.assetType || 'N/A'}
                              </small>
                            </div>
                            <span className={`badge ${editingAssignment.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {editingAssignment.isActive ? 'Active' : 'Returned'}
                            </span>
                          </div>
                          <div className="mt-3">
                            <label className="form-label fw-bold text-primary">
                              <i className="fas fa-barcode me-2"></i>Serial Number
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter serial number"
                              value={formData.selectedAssets[0]?.serialNumber || ''}
                              onChange={(e) => {
                                const updatedAssets = [...formData.selectedAssets];
                                if (updatedAssets[0]) {
                                  updatedAssets[0] = { ...updatedAssets[0], serialNumber: e.target.value };
                                  setFormData({ ...formData, selectedAssets: updatedAssets });
                                }
                              }}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="card border-0" style={{
                      backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#2d3748' : '#f8f9fa'
                    }}>
                      <div className="card-body">
                        <label className="form-label fw-bold text-primary">
                          <i className="fas fa-calendar-alt me-2"></i>Assigned Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.assignedDate}
                          onChange={(e) => setFormData({ ...formData, assignedDate: e.target.value })}
                          required
                          style={{
                            backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#4a5568' : '#ffffff',
                            color: document.body.getAttribute('data-theme') === 'dark' ? '#e2e8f0' : '#495057',
                            border: document.body.getAttribute('data-theme') === 'dark' ? '1px solid #4a5568' : '1px solid #ced4da'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0" style={{
                      backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#2d3748' : '#f8f9fa'
                    }}>
                      <div className="card-body">
                        <label className="form-label fw-bold text-primary">
                          <i className="fas fa-shipping-fast me-2"></i>Delivery Method
                        </label>
                        <select
                          className="form-select"
                          value={formData.deliveryMethod}
                          onChange={(e) => {
                            const deliveryMethod = parseInt(e.target.value);
                            setFormData({
                              ...formData,
                              deliveryMethod,
                              deliveryStatus: editingAssignment && deliveryMethod === 0 ? 2 : formData.deliveryStatus,
                              courierService: deliveryMethod === 1 ? formData.courierService : '',
                              trackingId: deliveryMethod === 1 ? formData.trackingId : '',
                              pickupPersonName: deliveryMethod === 2 ? formData.pickupPersonName : '',
                              pickupPersonMobile: deliveryMethod === 2 ? formData.pickupPersonMobile : ''
                            });
                          }}
                          required
                          style={{
                            backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#4a5568' : '#ffffff',
                            color: document.body.getAttribute('data-theme') === 'dark' ? '#e2e8f0' : '#495057',
                            border: document.body.getAttribute('data-theme') === 'dark' ? '1px solid #4a5568' : '1px solid #ced4da'
                          }}
                        >
                          <option value={0}>🚶 Self Pickup</option>
                          <option value={1}>🚚 Courier</option>
                          <option value={2}>👥 Other Person</option>
                        </select>
                        {editingAssignment && (
                          <div className="mt-3">
                            <label className="form-label fw-bold text-primary">
                              <i className="fas fa-truck-loading me-2"></i>Delivery Status
                            </label>
                            <select
                              className="form-select"
                              value={formData.deliveryStatus}
                              onChange={(e) => setFormData({ ...formData, deliveryStatus: parseInt(e.target.value) })}
                            >
                              <option value={0}>Pending</option>
                              <option value={1}>In Transit</option>
                              <option value={2}>Delivered</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0" style={{
                      backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#2d3748' : '#f8f9fa'
                    }}>
                      <div className="card-body">
                        <label className="form-label fw-bold text-primary">
                          <i className="fas fa-sticky-note me-2"></i>Notes
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Add any special instructions..."
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          style={{
                            backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#4a5568' : '#ffffff',
                            color: document.body.getAttribute('data-theme') === 'dark' ? '#e2e8f0' : '#495057',
                            border: document.body.getAttribute('data-theme') === 'dark' ? '1px solid #4a5568' : '1px solid #ced4da'
                          }}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
                
                {formData.deliveryMethod === 1 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="card border-warning">
                        <div className="card-header bg-warning text-dark">
                          <h6 className="mb-0">
                            <i className="fas fa-truck me-2"></i>Courier Details
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                <i className="fas fa-building me-2"></i>Courier Service
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., FedEx, DHL, Blue Dart"
                                value={formData.courierService}
                                onChange={(e) => setFormData({ ...formData, courierService: e.target.value })}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                <i className="fas fa-tracking me-2"></i>Tracking ID
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter tracking number"
                                value={formData.trackingId}
                                onChange={(e) => setFormData({ ...formData, trackingId: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.deliveryMethod === 2 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="card border-info">
                        <div className="card-header bg-info text-white">
                          <h6 className="mb-0">
                            <i className="fas fa-user-friends me-2"></i>Pickup Person Details
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                <i className="fas fa-user me-2"></i>Person Name
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Full name of pickup person"
                                value={formData.pickupPersonName}
                                onChange={(e) => setFormData({ ...formData, pickupPersonName: e.target.value })}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                <i className="fas fa-phone me-2"></i>Mobile Number
                              </label>
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="10-digit mobile number"
                                value={formData.pickupPersonMobile}
                                onChange={(e) => setFormData({ ...formData, pickupPersonMobile: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light border-0 p-4">
                <button type="button" className="btn btn-outline-secondary btn-lg" onClick={handleCloseModal}>
                  <i className="fas fa-times me-2"></i>Cancel
                </button>
                <button 
                  type="submit" 
                  className={`btn btn-primary btn-lg ${saving ? 'btn-loading' : ''}`} 
                  disabled={saving || formData.selectedAssets.length === 0 || !formData.employeeId || formData.employeeId === '' || (!editingAssignment && formData.selectedAssets.some(a => !a.serialNumber.trim()))}
                  style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none'}}
                >
                  {saving ? (
                    <>
                      <Loader size="small" />
                      <span className="ms-2">Processing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic me-2"></i>
                      {editingAssignment
                        ? 'Update Assignment'
                        : `Assign ${formData.selectedAssets.length} Asset${formData.selectedAssets.length !== 1 ? 's' : ''}`}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Transfer Modal */}
      <div className={`modal fade ${showTransferModal ? 'show d-block' : ''}`} style={{backgroundColor: showTransferModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exchange-alt me-2"></i>
                Transfer Asset
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTransferModal(false)}></button>
            </div>
            <form onSubmit={handleTransferSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">From Employee & Asset</label>
                  <SearchableSelect
                    options={assignments.filter(a => a.isActive).map(assignment => ({
                      value: assignment.id,
                      label: `${assignment.employeeName || assignment.EmployeeName || assignment.employeeId || assignment.EmployeeId || 'Unknown'} - ${assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset'} (${assignment.saralAssetId || 'N/A'})`
                    }))}
                    value={transferData.assignmentId}
                    onChange={(e) => setTransferData({ ...transferData, assignmentId: e.target.value })}
                    placeholder="Select current assignment"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">To Employee</label>
                  <SearchableSelect
                    options={employees.map(employee => ({ 
                      value: employee.id ?? employee.Id,
                      label: employee.userName || employee.UserName || employee.name || employee.Name || (employee.id ?? employee.Id)
                    }))}
                    value={transferData.toEmployeeId}
                    onChange={(e) => {
                      console.log('Selected employee ID:', e.target.value); // Debug log
                      setTransferData({ ...transferData, toEmployeeId: e.target.value });
                    }}
                    placeholder="Select new employee"
                    required
                  />
                  <small className="text-muted">Current value: {transferData.toEmployeeId || 'empty'}</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Transfer Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter transfer reason/notes"
                    value={transferData.notes}
                    onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTransferModal(false)}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving || !transferData.assignmentId || !transferData.toEmployeeId}>
                  {saving ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      <i className="fas fa-exchange-alt me-1"></i>
                      Transfer Asset
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      <div className={`modal fade ${showQRModal ? 'show d-block' : ''}`} style={{backgroundColor: showQRModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-qrcode me-2"></i>
                Assignment QR Code
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssignment && (
                <QRCodeGenerator 
                  asset={{
                    id: selectedAssignment.id,
                    name: `${selectedAssignment.asset?.name || selectedAssignment.asset?.Name || 'Unknown Asset'} - ${selectedAssignment.employeeName || selectedAssignment.EmployeeName || selectedAssignment.employeeId || selectedAssignment.EmployeeId || 'Unknown'}`,
                    saralAssetId: selectedAssignment.saralAssetId,
                    serialNumber: selectedAssignment.serialNumber,
                    assetType: selectedAssignment.asset?.assetType
                  }} 
                  onClose={() => setShowQRModal(false)} 
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Modal */}
      <div className={`modal fade ${showTimelineModal ? 'show d-block' : ''}`} style={{backgroundColor: showTimelineModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg timeline-modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-history me-2"></i>
                Asset Timeline
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTimelineModal(false)}></button>
            </div>
            <div className="modal-body timeline-modal-body">
              {assetTimeline.length > 0 ? (
                <div className="timeline">
                  {assetTimeline.map((event, index) => (
                    <div key={index} className="timeline-item mb-3">
                      <div className="d-flex">
                        <div className="timeline-marker me-3">
                          <div className={`rounded-circle p-2 ${event.type === 'assignment' ? 'bg-success' : event.type === 'return' ? 'bg-warning' : 'bg-info'}`}>
                            <i className={`fas ${event.type === 'assignment' ? 'fa-user-plus' : event.type === 'return' ? 'fa-undo' : 'fa-exchange-alt'} text-white`}></i>
                          </div>
                        </div>
                        <div className="timeline-content flex-grow-1">
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="card-title mb-1">{event.action}</h6>
                              <p className="card-text text-muted mb-2">{event.description}</p>
                              <small className="text-muted">
                                <i className="fas fa-clock me-1"></i>
                                {formatDisplayDateTimeIST(event.date)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-history fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No timeline data available for this asset.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <AssignmentBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={() => {
          fetchAssignments();
          fetchAvailableAssets();
        }}
      />

      <div className={`modal fade ${showDeleteAllConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteAllConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete All</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteAllConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete ALL {assignments.length} assignments permanently!</p>
              <p>This action cannot be undone. Are you absolutely sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteAllConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteAll}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeleteSelectedConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteSelectedConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete Selected</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteSelectedConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete {selectedIds.length} selected assignment(s) permanently!</p>
              <p>This action cannot be undone. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteSelectedConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetAssignments;
